import { useIonAlert } from '@ionic/react';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BleService } from 'src/ble/BleService';
import { MobileBundleCard } from 'src/components/reusable-components/mobile-bundle-card/MobileBundleCard';
import { BundleMessages } from 'src/intl/BundleMessages';
import { GenericMessages } from 'src/intl/GenericMessages';
import { selectBleIsLocked } from 'src/redux/ble/selectors';
import { BleControllerBundle } from 'src/redux/ble/types';
import { isValidBundlePattern } from 'src/utils/helpers/bundleParserHelpers';
import { AppRouteConstants } from '../app-layout/AppRouteConstants';
import { useToast } from 'src/components/reusable-components/useToast';
import { useGetBundlesQuery } from 'src/redux/api/bundles';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { useSocketGet } from 'src/utils/socket';

interface ControllerInfoBundleCardProps {
  file: BleControllerBundle;
}

export const ControllerInfoBundleCard: React.FC<ControllerInfoBundleCardProps> = ({ file }) => {
  const { formatMessage } = useIntl();
  const [alert] = useIonAlert();
  const [present] = useToast();
  const isLocked = useSelector(selectBleIsLocked);
  const { data: bundle } = useGetBundlesQuery(file.bundleId);
  const { item: bundleOrg } = useSocketGet(SocketContextSentinel, 'organizations', bundle?.organizationId);

  const bundleName = bundle?.name ?? file?.fileName;
  const isMissingInformation = !bundle;

  const onConfirmDelete = useCallback(async () => {
    try {
      if (!BleService.controller) throw new Error('No controller connected!');
      await BleService.controller.deleteBundle(file.bundleId);
      await present(
        {
          message: formatMessage(BundleMessages.uninstallSuccess, { bundleName }),
        },
        'success',
      );
    } catch (err) {
      present(
        {
          message: formatMessage(BundleMessages.installFailure, { bundleName }),
        },
        'danger',
      );
    }
  }, [bundleName, file.bundleId, formatMessage, present]);

  const action = useMemo(() => {
    return {
      text: formatMessage(BundleMessages.uninstallButton),
      disabled: isLocked,
      color: 'secondary',
      onPress: async () => {
        if (isMissingInformation) {
          await alert(formatMessage(BundleMessages.uninstallMissingConfirmation, { bundleName }), [
            { role: 'cancel', text: formatMessage(GenericMessages.cancelButtonLabel) },
            {
              role: 'destructive',
              text: formatMessage(GenericMessages.delete),
              handler: onConfirmDelete,
            },
          ]);
        } else {
          await onConfirmDelete();
        }
      },
    };
  }, [alert, bundleName, formatMessage, isLocked, isMissingInformation, onConfirmDelete]);

  const tubes = isValidBundlePattern(bundle?.pattern)
    ? { count: bundle!.tubes.length, pattern: bundle!.pattern }
    : undefined;

  if (isMissingInformation) return null; // In the future, the user will be able to view bundles that only exist in the controller and not on sentinel link. For now, they are hidden.

  const routerLink = bundle ? AppRouteConstants.BundleDetails.replace(':id', bundle._id) : undefined;
  return (
    <MobileBundleCard
      bundleName={bundleName}
      siteName={bundle?.siteName ?? file.siteName}
      orgName={bundleOrg?.orgName}
      routerLink={routerLink}
      tubes={tubes}
      model={bundle?.heatExchangerModelNumber ?? undefined}
      createdAt={bundle?.createdAt ?? new Date(file.createdAt).getTime()}
      action={action}
      isMissingInfo={isMissingInformation}
    />
  );
};
